import React, { FC } from "react";

/**
 * The line icons the desktop workspace shares. Drawn here rather than pulled
 * from a set: the phone steps already hand-draw theirs from the spec artwork,
 * and these keep the same 1.5-weight stroke so the two read as one product.
 */

export interface IconProps {
  className?: string;
}

const base = (className: string) => `flex-none ${className}`;

export const ArrowLeftIcon: FC<IconProps> = ({ className = "h-4 w-4" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5m0 0l7 7m-7-7l7-7" />
  </svg>
);

export const ArrowRightIcon: FC<IconProps> = ({ className = "h-4 w-4" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14m0 0l-7-7m7 7l-7 7" />
  </svg>
);

export const ArrowUpIcon: FC<IconProps> = ({ className = "h-5 w-5" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20V4m0 0l-6 6m6-6l6 6" />
  </svg>
);

export const CheckIcon: FC<IconProps> = ({ className = "h-3.5 w-3.5" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

export const CheckCircleIcon: FC<IconProps> = ({ className = "h-4 w-4" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12l2.5 2.5L16 9" />
  </svg>
);

export const InfoIcon: FC<IconProps> = ({ className = "h-4 w-4" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11.2v5.2" />
    <path d="M12 7.9v.1" />
  </svg>
);

export const ShieldCheckIcon: FC<IconProps> = ({ className = "h-5 w-5" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 21.5c4.6-2 6.8-5.2 6.8-9.4V5.9L12 3.2 5.2 5.9v6.2c0 4.2 2.2 7.4 6.8 9.4z" />
    <path d="M9.2 12.2l2 2 3.6-3.8" />
  </svg>
);

export const LockIcon: FC<IconProps> = ({ className = "h-5 w-5" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4.5" y="10" width="15" height="11" rx="2.5" />
    <path d="M8 10V7.5a4 4 0 018 0V10" />
    <circle cx="12" cy="14.8" r="1.3" />
    <path d="M12 16.1v2.4" />
  </svg>
);

/** The small rotated square that marks the rail's footer note. */
export const DiamondIcon: FC<IconProps> = ({ className = "h-3.5 w-3.5" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l9 9-9 9-9-9z" />
  </svg>
);

export const PrinterIcon: FC<IconProps> = ({ className = "h-4 w-4" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 9V3.5h10V9" />
    <rect x="3.5" y="9" width="17" height="7.5" rx="2" />
    <path d="M7 14h10v6.5H7z" />
  </svg>
);

export const HomeHeartIcon: FC<IconProps> = ({ className = "h-8 w-8" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.3}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2.6 11.6L12 4l9.4 7.6" />
    <path d="M16.8 6.6V4.3h1.9v3.8" />
    <path d="M4.9 10v9.6h14.2V10" />
    <path d="M12 17c-2.5-1.7-3.7-2.9-3.7-4.2a2 2 0 013.7-1.1 2 2 0 013.7 1.1c0 1.3-1.2 2.5-3.7 4.2z" />
  </svg>
);

export const PersonIcon: FC<IconProps> = ({ className = "h-5 w-5" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="3.4" />
    <path d="M6 19.5c0-3.3 2.7-5.6 6-5.6s6 2.3 6 5.6z" />
  </svg>
);

export const EyeIcon: FC<IconProps> = ({ className = "h-3.5 w-3.5" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const PlusIcon: FC<IconProps> = ({ className = "h-4 w-4" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const PhoneIcon: FC<IconProps> = ({ className = "h-4 w-4" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1 1 .4 1.9.7 2.8a2 2 0 01-.4 2.1L8.1 9.9a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.8.7a2 2 0 011.7 2z" />
  </svg>
);

/** The nib the signature card centres in its cream disc. */
export const PenIcon: FC<IconProps> = ({ className = "h-4 w-4" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16.8 3.9a2.1 2.1 0 013 3L8.6 18.1l-4 1 1-4z" />
    <path d="M14.6 6.1l3.3 3.3" />
  </svg>
);

/** The ↗ on "Open document" — the document opens away from the flow. */
export const ExternalLinkIcon: FC<IconProps> = ({ className = "h-3 w-3" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 16L16.5 7.5" />
    <path d="M9.5 7.5h7v7" />
  </svg>
);

export const MailIcon: FC<IconProps> = ({ className = "h-4 w-4" }) => (
  <svg
    className={base(className)}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);
